import { useCallback, useEffect, useRef } from 'react';
import {
  serializeForClipboard,
  isValidFokusClipboard,
  cloneTaskFromClipboard,
  remapDependencies,
  attachNestedSprintsFromClipboard,
  collectNestedSprintData,
  generateCustomFieldIds,
} from '../../shared/clipboardUtils';
import { shouldDeferToNativeClipboardShortcuts } from '../../FlowchartView/hooks/utils/overlayPointerGuard';

/**
 * useTodoClipboard Hook
 * 
 * Handles copy/paste/duplicate operations for TodoList items using system clipboard.
 * Uses the same JSON format as FlowchartView for cross-view compatibility.
 * 
 * - Ctrl+C copies selected items to system clipboard as JSON
 * - Ctrl+V pastes from system clipboard (if valid Fokus format)
 * - Ctrl+D duplicates selected items
 * - Delete/Backspace deletes selected items
 * - Ctrl+A selects all items
 */
export function useTodoClipboard({
  tasks,
  selectedTaskIds,
  setSelectedTaskIds,
  nestedSprints = [],
  allSprints = [],
  allTags = [],
  customFields = [],
  onPasteTasks,
  onDeleteTask,
  scrollToTask, // Optional callback to scroll to a task after paste
}) {
  // Keep a local ref for duplicate (copy+paste in same action)
  const lastCopiedRef = useRef(null);

  /**
   * Copy selected items to system clipboard
   */
  const copySelectedItems = useCallback(async () => {
    if (!selectedTaskIds || selectedTaskIds.size === 0) return false;
    
    const selectedIds = Array.from(selectedTaskIds);
    const selectedTasks = tasks.filter(t => selectedIds.includes(t.id));
    
    if (selectedTasks.length === 0) return false;
    
    // For TodoList, we don't have meaningful positions, so use simple relative positions
    const relativePositions = {};
    selectedTasks.forEach((task, idx) => {
      relativePositions[task.id] = { x: 0, y: idx * 50 }; // Stack vertically
    });
    
    // Collect nested sprint data
    const nestedSprintData = collectNestedSprintData(selectedTasks, nestedSprints, allSprints);
    
    // Serialize for clipboard (include tag definitions for color preservation, custom fields)
    const clipboardData = serializeForClipboard(selectedTasks, relativePositions, nestedSprintData, allTags, customFields);
    
    // Store locally for duplicate action
    lastCopiedRef.current = clipboardData;
    
    // Write to system clipboard
    try {
      await navigator.clipboard.writeText(JSON.stringify(clipboardData, null, 2));
      console.log(`📋 Copied ${selectedTasks.length} item(s) to clipboard (JSON format)`);
      return true;
    } catch (error) {
      console.error('Failed to write to clipboard:', error);
      return true; // Still stored locally
    }
  }, [selectedTaskIds, tasks, nestedSprints, allTags, customFields]);

  /**
   * Paste items from system clipboard
   */
  const pasteItems = useCallback(async (clipboardDataOverride = null) => {
    if (!onPasteTasks) return false;
    
    let clipboardData = clipboardDataOverride;
    
    // Try to read from system clipboard if no override provided
    if (!clipboardData) {
      try {
        const text = await navigator.clipboard.readText();
        clipboardData = JSON.parse(text);
      } catch (error) {
        // Fallback to local ref (for duplicate action or clipboard access denied)
        clipboardData = lastCopiedRef.current;
      }
    }
    
    // Validate clipboard data
    if (!isValidFokusClipboard(clipboardData)) {
      console.log('📋 Clipboard does not contain valid Fokus data');
      return false;
    }
    
    const { tasks: clipboardTasks, nestedSprints: clipboardNestedSprints, tagDefinitions = [], customFieldDefinitions = [] } = clipboardData;
    
    const timestamp = Date.now();
    const idMap = new Map();
    const optionIdMap = new Map();
    
    // Generate new IDs for custom field definitions (v2 uses indices)
    const { 
      definitions: newCustomFieldDefs, 
      fieldIdMap: customFieldIdMap, 
      optionIdMap: customFieldOptionIdMap 
    } = generateCustomFieldIds(customFieldDefinitions, timestamp);
    
    // Clone all tasks with custom field ID remapping
    const clonedTasks = clipboardTasks.map((task, idx) => 
      cloneTaskFromClipboard(task, idMap, optionIdMap, timestamp, idx, customFieldIdMap, customFieldOptionIdMap)
    );
    
    // Remap dependencies to new IDs
    remapDependencies(clonedTasks, idMap, optionIdMap);
    
    // For TodoList, positions don't matter much
    const pastedPositions = {};
    clonedTasks.forEach(task => {
      pastedPositions[task.id] = { x: 0, y: 0 };
    });
    
    const clonedNestedSprints = attachNestedSprintsFromClipboard(
      clonedTasks,
      clipboardNestedSprints,
      timestamp,
    );
    clonedTasks.forEach(task => {
      delete task._clipboardPos;
    });
    
    console.log(`📋 Pasting ${clonedTasks.length} item(s)`);
    
    // Call the paste handler (include tag definitions for color preservation, custom field definitions with new IDs)
    onPasteTasks({
      tasks: clonedTasks,
      positions: pastedPositions,
      nestedSprints: clonedNestedSprints,
      tagDefinitions,
      customFieldDefinitions: newCustomFieldDefs,
    });
    
    // Select the pasted items
    const newIds = clonedTasks.map(t => t.id);
    if (setSelectedTaskIds) {
      setSelectedTaskIds(new Set(newIds));
    }
    
    // Scroll to the first pasted item after a brief delay
    if (scrollToTask && newIds.length > 0) {
      setTimeout(() => {
        scrollToTask(newIds[0]);
      }, 100);
    }
    
    return true;
  }, [onPasteTasks, setSelectedTaskIds, scrollToTask]);

  /**
   * Duplicate selected items (copy + paste in one action)
   */
  const duplicateSelectedItems = useCallback(async () => {
    if (await copySelectedItems()) {
      await pasteItems(lastCopiedRef.current);
    }
  }, [copySelectedItems, pasteItems]);

  /**
   * Delete selected items
   */
  const deleteSelectedItems = useCallback(() => {
    if (!selectedTaskIds || selectedTaskIds.size === 0 || !onDeleteTask) return;
    
    const idsToDelete = Array.from(selectedTaskIds);
    console.log(`🗑️ Deleting ${idsToDelete.length} item(s)`);
    
    // Delete each task individually (TodoListView uses single delete)
    idsToDelete.forEach(id => onDeleteTask(id));
    if (setSelectedTaskIds) {
      setSelectedTaskIds(new Set());
    }
  }, [selectedTaskIds, onDeleteTask, setSelectedTaskIds]);

  /**
   * Keyboard shortcut handler
   */
  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.repeat) return;
      if (shouldDeferToNativeClipboardShortcuts(e)) {
        return;
      }
      
      // Ctrl+C or Cmd+C - Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedTaskIds && selectedTaskIds.size > 0) {
          e.preventDefault();
          await copySelectedItems();
        }
      }
      
      // Ctrl+X or Cmd+X - Cut (copy then delete)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        if (selectedTaskIds && selectedTaskIds.size > 0) {
          e.preventDefault();
          const copied = await copySelectedItems();
          if (copied && onDeleteTask) {
            const idsToDelete = Array.from(selectedTaskIds);
            idsToDelete.forEach(id => onDeleteTask(id));
            if (setSelectedTaskIds) {
              setSelectedTaskIds(new Set());
            }
          }
        }
      }
      
      // Ctrl+V or Cmd+V - Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        await pasteItems();
      }
      
      // Ctrl+D or Cmd+D - Duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        if (selectedTaskIds && selectedTaskIds.size > 0) {
          e.preventDefault();
          await duplicateSelectedItems();
        }
      }
      
      // Delete or Backspace - Delete selected
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedTaskIds && selectedTaskIds.size > 0) {
        e.preventDefault();
        deleteSelectedItems();
      }
      
      // Escape - Clear selection
      if (e.key === 'Escape' && setSelectedTaskIds) {
        setSelectedTaskIds(new Set());
      }
      
      // Ctrl+A or Cmd+A - Select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && setSelectedTaskIds) {
        e.preventDefault();
        setSelectedTaskIds(new Set(tasks.map(t => t.id)));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTaskIds, tasks, copySelectedItems, pasteItems, duplicateSelectedItems, deleteSelectedItems, setSelectedTaskIds, onDeleteTask]);

  /**
   * Check if clipboard has valid Fokus data
   */
  const checkClipboardData = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const data = JSON.parse(text);
      return isValidFokusClipboard(data);
    } catch {
      return !!lastCopiedRef.current;
    }
  }, []);

  return {
    copySelectedItems,
    pasteItems,
    duplicateSelectedItems,
    deleteSelectedItems,
    hasClipboardData: checkClipboardData,
  };
}

export default useTodoClipboard;
